/**
 * Product lines — **the source of truth for value** (§6.11). There are no
 * header mirrors: everything on the right rail rolls up from these rows
 * through `v_opportunity_effective`.
 *
 * Two rules are visible on screen rather than only in the query:
 *
 *  - **`alternate` lines are visually distinct and excluded from the displayed
 *    total**, with the total labelled — they are competing parts for a socket a
 *    primary line already counts. On the real distributor file the exclusion is
 *    worth $510,585 (5.2%), and a column that does not add up with no
 *    explanation is a support ticket;
 *  - **there is deliberately no unique "primary" line.** 29 of 76 real
 *    opportunities carry two to four, because "primary" means
 *    primary-versus-alternate *for a socket* and a multi-socket system
 *    legitimately has several.
 *
 * The live gap between ASP and the customer's target price is what makes
 * `PRICE_TOO_HIGH` predictable rather than post-hoc.
 *
 * **EAU is units per board × boards per year when both are set**
 * (`docs/bugs.md` session 2 part 3, decision 11 — reversing FR-0001). On a new
 * line and on an existing one, the moment both fields hold a value the Annual
 * units field turns read-only and shows the product with its working
 * ("4 × 25,000"), recalculating as either changes. The server enforces the same
 * rule and refuses a disagreeing `annual_units`, so this is a display of the
 * rule, not the rule. When either field is empty — every imported line — EAU is
 * typed as before.
 */

import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { LINE_TYPES, calculatedAnnualUnits } from '@menlo/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useApiMutation, useApiQuery, type Paged, type Row } from '@/lib/queries';
import { useBusinessUnits, useReferenceData } from '@/lib/reference';
import { formatMoney, formatUnits, humanize, multiplyMoney, sumMoney } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * An optional integer column, as a form value. `null` reads as empty rather
 * than as `0`, because on this table zero is a real quantity — `design_support`
 * lines routinely carry `annual_units = 0` — and "unset" and "zero" must not
 * render the same.
 */
function optionalText(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

/** Empty clears the column; everything else is an integer, zero included. */
function toOptionalInt(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : Number(trimmed);
}

/** "4 × 25,000" — the working shown beside a calculated EAU. */
function working(perBoard: number, boards: number): string {
  return `${perBoard.toLocaleString('en-US')} × ${boards.toLocaleString('en-US')}`;
}

/**
 * The calculated EAU, read-only, with its working underneath. An input rather
 * than text so it sits in the same place and the same width as the typed one
 * it replaces, and so a screen reader still finds "Annual units".
 */
function CalculatedUnits({
  units,
  perBoard,
  boards,
  className,
}: {
  units: number;
  perBoard: number;
  boards: number;
  className?: string;
}): React.ReactElement {
  return (
    <span className="inline-flex flex-col items-end">
      <Input
        aria-label="Annual units"
        readOnly
        tabIndex={-1}
        title="Units per board × annual boards. Clear either one to type EAU by hand."
        className={cn('cursor-default bg-muted/50 text-right', className)}
        value={units.toLocaleString('en-US')}
      />
      <span className="text-[11px] text-muted-foreground" data-testid="eau-working">
        {working(perBoard, boards)}
      </span>
    </span>
  );
}

export function ProductLines({
  opportunityId,
  priceBasis,
  canWrite,
}: {
  opportunityId: string;
  priceBasis: string;
  canWrite: boolean;
}): React.ReactElement {
  const reference = useReferenceData();
  const [adding, setAdding] = useState(false);

  const key = ['opportunity-products', opportunityId];
  const lines = useApiQuery<Paged<Row>>(
    key,
    `/api/opportunity-products?opportunityId=${opportunityId}&pageSize=100`,
  );

  const update = useApiMutation<
    Record<string, unknown>,
    { id: string; body: Record<string, unknown> }
  >(({ id, body }) => ({ path: `/api/opportunity-products/${id}`, method: 'PUT', body }), {
    invalidate: [key, ['opportunity', opportunityId]],
  });

  const remove = useApiMutation<Record<string, unknown>, string>(
    (id) => ({ path: `/api/opportunity-products/${id}`, method: 'DELETE' }),
    { invalidate: [key, ['opportunity', opportunityId]] },
  );

  const items = lines.data?.items ?? [];
  const counted = items.filter((line) => line['line_type'] !== 'alternate');
  const total = sumMoney(
    counted.map((line) => multiplyMoney(line['annual_units'] as string, line['asp'] as string)),
  );
  const excluded = items.length - counted.length;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Product lines</h3>
        {canWrite && !adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Add line
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="px-2 py-1.5 text-left">
                Part number
              </th>
              <th scope="col" className="px-2 py-1.5 text-left">
                Business unit
              </th>
              <th
                scope="col"
                className="px-2 py-1.5 text-right"
                title="Parts per board or system. Context only — it is not part of any value sum."
              >
                Units/board
              </th>
              <th
                scope="col"
                className="px-2 py-1.5 text-right"
                title="Boards or systems per year. Context only — it is not part of any value sum."
              >
                Annual boards
              </th>
              <th scope="col" className="px-2 py-1.5 text-right">
                Annual units
              </th>
              <th scope="col" className="px-2 py-1.5 text-right">
                {priceBasis === 'distributor_resale' ? 'Resale ASP' : 'ASP'}
              </th>
              <th scope="col" className="px-2 py-1.5 text-right">
                Target ASP
              </th>
              <th scope="col" className="px-2 py-1.5 text-right">
                Gap
              </th>
              <th scope="col" className="px-2 py-1.5 text-left">
                Line type
              </th>
              <th scope="col" className="px-2 py-1.5 text-right">
                Annual value
              </th>
              {canWrite && <th scope="col" className="w-8" />}
            </tr>
          </thead>
          <tbody>
            {items.map((line) => (
              <LineRow
                key={String(line['id'])}
                line={line}
                canWrite={canWrite}
                onUpdate={(body) => update.mutate({ id: String(line['id']), body })}
                onRemove={() => remove.mutate(String(line['id']))}
              />
            ))}

            {items.length === 0 && (
              <tr>
                <td colSpan={11} className="px-2 py-6 text-center text-xs text-muted-foreground">
                  No lines yet. Value rolls up from these, so the deal is worth nothing until one
                  exists.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t bg-muted/30 font-medium">
              <td colSpan={9} className="px-2 py-1.5 text-right text-xs">
                Annual value{' '}
                {priceBasis === 'distributor_resale' ? '(customer resale)' : '(Menlo net)'},
                excluding {excluded} alternate line{excluded === 1 ? '' : 's'}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{formatMoney(total)}</td>
              {canWrite && <td />}
            </tr>
          </tfoot>
        </table>
      </div>

      {adding && (
        <AddLine
          opportunityId={opportunityId}
          products={reference.products}
          invalidate={key}
          onDone={() => setAdding(false)}
        />
      )}
    </section>
  );
}

/**
 * One product line. Units per board and annual boards are held locally while
 * they are typed, so the calculated EAU follows them keystroke by keystroke;
 * each is saved on blur, and the server — which writes `annual_units` from the
 * pair — is what the refetch then shows.
 */
function LineRow({
  line,
  canWrite,
  onUpdate,
  onRemove,
}: {
  line: Row;
  canWrite: boolean;
  onUpdate: (body: Record<string, unknown>) => void;
  onRemove: () => void;
}): React.ReactElement {
  const businessUnitLabel = useBusinessUnits().label;
  const storedPerBoard = optionalText(line['units_per_board']);
  const storedBoards = optionalText(line['annual_boards']);
  const [perBoardDraft, setPerBoardDraft] = useState(storedPerBoard);
  const [boardsDraft, setBoardsDraft] = useState(storedBoards);
  useEffect(() => setPerBoardDraft(storedPerBoard), [storedPerBoard]);
  useEffect(() => setBoardsDraft(storedBoards), [storedBoards]);

  const perBoard = toOptionalInt(perBoardDraft);
  const boards = toOptionalInt(boardsDraft);
  const calculated = calculatedAnnualUnits(perBoard, boards);
  const units = calculated === null ? String(line['annual_units']) : String(calculated);

  const alternate = line['line_type'] === 'alternate';
  const asp = Number(line['asp']);
  const target = line['customer_target_asp'];
  const gap = target === null || target === undefined ? null : asp - Number(target);

  /** Save one of the pair, if it changed. Empty clears it. */
  const savePair = (column: 'units_per_board' | 'annual_boards', raw: string): void => {
    const stored = column === 'units_per_board' ? storedPerBoard : storedBoards;
    if (raw.trim() === stored) return;
    onUpdate({ [column]: toOptionalInt(raw) });
  };

  return (
    <tr className={cn('border-t', alternate && 'bg-muted/40 text-muted-foreground')}>
      <td className="px-2 py-1.5 font-mono text-xs">{String(line['part_number'])}</td>
      <td className="px-2 py-1.5">
        <Badge variant="outline">{businessUnitLabel(String(line['business_unit']))}</Badge>
      </td>
      <td className="px-2 py-1.5 text-right">
        {canWrite ? (
          <Input
            aria-label="Units per board"
            className="h-7 w-20 text-right text-sm"
            value={perBoardDraft}
            inputMode="numeric"
            onChange={(event) => setPerBoardDraft(event.target.value)}
            onBlur={(event) => savePair('units_per_board', event.target.value)}
          />
        ) : (
          storedPerBoard || '—'
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {canWrite ? (
          <Input
            aria-label="Annual boards"
            className="h-7 w-24 text-right text-sm"
            value={boardsDraft}
            inputMode="numeric"
            onChange={(event) => setBoardsDraft(event.target.value)}
            onBlur={(event) => savePair('annual_boards', event.target.value)}
          />
        ) : (
          storedBoards || '—'
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {calculated !== null && perBoard !== null && boards !== null ? (
          canWrite ? (
            <CalculatedUnits
              units={calculated}
              perBoard={perBoard}
              boards={boards}
              className="h-7 w-28 text-sm"
            />
          ) : (
            <span className="inline-flex flex-col items-end">
              <span>{formatUnits(calculated)}</span>
              <span className="text-[11px] text-muted-foreground" data-testid="eau-working">
                {working(perBoard, boards)}
              </span>
            </span>
          )
        ) : canWrite ? (
          <Input
            // Re-mounted when the stored value moves, so a server-side change
            // (or the end of a calculation) shows rather than a stale draft.
            key={String(line['annual_units'])}
            aria-label="Annual units"
            className="h-7 w-28 text-right text-sm"
            defaultValue={String(line['annual_units'])}
            inputMode="numeric"
            onBlur={(event) => {
              if (event.target.value === String(line['annual_units'])) return;
              onUpdate({ annual_units: Number(event.target.value) });
            }}
          />
        ) : (
          formatUnits(line['annual_units'] as string)
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {canWrite ? (
          <Input
            aria-label="ASP"
            className="h-7 w-24 text-right text-sm"
            defaultValue={String(line['asp'])}
            inputMode="decimal"
            onBlur={(event) => onUpdate({ asp: event.target.value })}
          />
        ) : (
          formatMoney(line['asp'] as string, { exact: true })
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {canWrite ? (
          <Input
            aria-label="Customer target ASP"
            className="h-7 w-24 text-right text-sm"
            defaultValue={target === null || target === undefined ? '' : String(target)}
            inputMode="decimal"
            onBlur={(event) =>
              onUpdate({
                customer_target_asp: event.target.value === '' ? null : event.target.value,
              })
            }
          />
        ) : (
          formatMoney(target as string)
        )}
      </td>
      <td
        className={cn(
          'px-2 py-1.5 text-right tabular-nums',
          gap !== null && gap > 0 && 'font-medium text-red-700',
        )}
        title={
          gap === null
            ? undefined
            : gap > 0
              ? 'We are above the price the customer says they need.'
              : 'At or below the customer target.'
        }
      >
        {gap === null ? '—' : formatMoney(String(gap), { exact: true })}
      </td>
      <td className="px-2 py-1.5">
        {canWrite ? (
          <Select
            aria-label="Line type"
            className="h-7 w-32 text-xs"
            defaultValue={String(line['line_type'])}
            onChange={(event) => onUpdate({ line_type: event.target.value })}
          >
            {LINE_TYPES.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
        ) : (
          humanize(String(line['line_type']))
        )}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">
        {alternate ? (
          <span title="Excluded from every value sum — it competes with a line already counted.">
            ({formatMoney(multiplyMoney(units, line['asp'] as string))})
          </span>
        ) : (
          formatMoney(multiplyMoney(units, line['asp'] as string))
        )}
      </td>
      {canWrite && (
        <td className="px-1">
          <button
            type="button"
            aria-label={`Delete line ${String(line['part_number'])}`}
            onClick={onRemove}
            className="rounded p-1 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </td>
      )}
    </tr>
  );
}

function AddLine({
  opportunityId,
  products,
  invalidate,
  onDone,
}: {
  opportunityId: string;
  products: { id: string; part_number: string }[];
  invalidate: readonly unknown[];
  onDone: () => void;
}): React.ReactElement {
  const [productId, setProductId] = useState('');
  const [units, setUnits] = useState('');
  const [asp, setAsp] = useState('');
  const [lineType, setLineType] = useState('primary');
  const [unitsPerBoard, setUnitsPerBoard] = useState('');
  const [annualBoards, setAnnualBoards] = useState('');

  const perBoard = toOptionalInt(unitsPerBoard);
  const boards = toOptionalInt(annualBoards);
  /**
   * Both present — and zero counts as present, because a zero-volume
   * `design_support` line is legitimate and must not be unreachable. While it
   * is non-null, EAU is this and nothing typed.
   */
  const calculated = calculatedAnnualUnits(perBoard, boards);

  const create = useApiMutation<Record<string, unknown>, void>(
    () => ({
      path: '/api/opportunity-products',
      method: 'POST',
      body: {
        opportunity_id: opportunityId,
        product_id: productId,
        // The product while both of the pair are set, which the server would
        // write anyway (and refuse to see contradicted); otherwise what was
        // typed.
        annual_units: calculated ?? Number(units),
        asp,
        line_type: lineType,
        ...(perBoard === null ? {} : { units_per_board: perBoard }),
        ...(boards === null ? {} : { annual_boards: boards }),
      },
    }),
    { invalidate: [invalidate, ['opportunity', opportunityId]], onSuccess: onDone },
  );

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border bg-card p-2">
      <Select
        aria-label="Part number"
        className="h-8 w-44 text-sm"
        value={productId}
        onChange={(event) => setProductId(event.target.value)}
      >
        <option value="">Part number…</option>
        {products.map((product) => (
          <option key={product.id} value={product.id}>
            {product.part_number}
          </option>
        ))}
      </Select>
      <Input
        aria-label="Units per board"
        placeholder="Units/board"
        className="h-8 w-28"
        inputMode="numeric"
        value={unitsPerBoard}
        onChange={(event) => setUnitsPerBoard(event.target.value)}
      />
      <Input
        aria-label="Annual boards"
        placeholder="Annual boards"
        className="h-8 w-32"
        inputMode="numeric"
        value={annualBoards}
        onChange={(event) => setAnnualBoards(event.target.value)}
      />
      {calculated !== null && perBoard !== null && boards !== null ? (
        <CalculatedUnits
          units={calculated}
          perBoard={perBoard}
          boards={boards}
          className="h-8 w-28"
        />
      ) : (
        <Input
          aria-label="Annual units"
          placeholder="EAU"
          className="h-8 w-28"
          inputMode="numeric"
          value={units}
          onChange={(event) => setUnits(event.target.value)}
        />
      )}
      <Input
        aria-label="ASP"
        placeholder="ASP"
        className="h-8 w-24"
        inputMode="decimal"
        value={asp}
        onChange={(event) => setAsp(event.target.value)}
      />
      <Select
        aria-label="Line type"
        className="h-8 w-36 text-sm"
        value={lineType}
        onChange={(event) => setLineType(event.target.value)}
      >
        {LINE_TYPES.map((value) => (
          <option key={value} value={value}>
            {humanize(value)}
          </option>
        ))}
      </Select>
      <Button
        size="sm"
        onClick={() => create.mutate()}
        disabled={productId === '' || (calculated === null && units === '') || asp === ''}
      >
        Add
      </Button>
      <Button size="sm" variant="ghost" onClick={onDone}>
        Cancel
      </Button>
    </div>
  );
}
